import { useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { CartContext } from '../context/CartContext';

const SHARED_CART_CLIENT_ID_KEY = 'sharedCartClientId';
const PENDING_MAX_AGE_MS = 30 * 60 * 1000;
const ACK_TIMEOUT_MS = 8000;

export function sharedCartNoticeText(reason, language) {
  const chinese = language === 'zh' || language === 'zf';
  if (reason === 'cart_changed') return chinese
    ? '购物车已更新，请确认菜品。'
    : 'Your cart has changed. Please check your items.';
  return chinese
    ? '请检查购物车，补选缺少的菜品。'
    : 'Please check your cart and add any missing items.';
}

function isTakeaway(tableNo) {
  if (!tableNo) return true;
  const t = String(tableNo).toLowerCase();
  return t === 'take_away' || t === 'take-away';
}

function getOrCreateClientId() {
  let id = sessionStorage.getItem(SHARED_CART_CLIENT_ID_KEY);
  if (!id) {
    id = `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SHARED_CART_CLIENT_ID_KEY, id);
  }
  return id;
}

// 共享购物车 hook
// 参数：restaurantUrl, tableNo, enabled
// 返回：partnerFirstAdd (最近一次"伙伴首次加菜"事件；使用后可调 clearPartnerFirstAdd 清空)
export function useSharedCart(restaurantUrl, tableNo, enabled = true) {
  const { setCartFromRemote, subscribeLocalChange } = useContext(CartContext);
  const [partnerFirstAdd, setPartnerFirstAdd] = useState(null);
  const [isSharedCartReady, setIsSharedCartReady] = useState(false);
  const [syncNotice, setSyncNotice] = useState(null);

  const socketRef = useRef(null);

  useEffect(() => {
    setIsSharedCartReady(false);
    if (!enabled || !restaurantUrl || !tableNo || isTakeaway(tableNo)) {
      setPartnerFirstAdd(null);
      return undefined;
    }

    const clientId = getOrCreateClientId();
    const serverUrl = process.env.REACT_APP_SERVER_URL;
    const socket = io(`${serverUrl}/shared-cart`, {
      transports: ['websocket', 'polling'],
      reconnection: true,
    });
    socketRef.current = socket;

    let active = true;
    let ready = false;
    let snapshot = null;
    const pendingKey = `sharedCartPending:${restaurantUrl}:${tableNo}`;
    let pending = [];
    try {
      const saved = JSON.parse(sessionStorage.getItem(pendingKey) || '[]');
      if (Array.isArray(saved)) pending = saved.filter(request => request?.opId && request.op?.type && Number.isFinite(request.createdAt));
    } catch (error) {
      console.error('[shared-cart] Unable to restore pending operations:', error);
    }
    let inFlight = null;
    let connectionGeneration = 0;
    let supportsOperationIds = false;
    let retryTimer = null;
    let join;
    const retiredEpochs = new Set();

    const persist = () => {
      try {
        if (pending.length) sessionStorage.setItem(pendingKey, JSON.stringify(pending));
        else sessionStorage.removeItem(pendingKey);
      } catch (error) {
        console.error('[shared-cart] Unable to save pending operations:', error);
      }
    };
    const cancelRetry = () => {
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = null;
    };
    const discardPending = (reason) => {
      if (pending.length) setSyncNotice({ reason, ts: Date.now() });
      pending = [];
      inFlight = null;
      persist();
    };

    const acceptSnapshot = (payload, force = false) => {
      if (!payload || !Array.isArray(payload.cart) || !payload.epoch) return false;
      if (retiredEpochs.has(payload.epoch)) return false;
      if (snapshot && payload.epoch === snapshot.epoch && payload.version < snapshot.version) return false;
      // An expired Redis key starts at version zero. A stale_cart response is
      // authoritative even then; never accept a late response from a retired batch.
      if (!force && payload.reason !== 'stale_cart' && snapshot && payload.version < snapshot.version) return false;
      if (snapshot && snapshot.epoch !== payload.epoch) retiredEpochs.add(snapshot.epoch);
      snapshot = payload;
      return true;
    };

    // Serialize local operations and apply an authoritative snapshot after the
    // queue drains. An early acknowledgement must not erase later local clicks.
    const pump = () => {
      if (!active || !ready || !socket.connected || inFlight) return;
      if (pending.some(request => Date.now() - request.createdAt >= PENDING_MAX_AGE_MS)) discardPending('expired');
      // Older servers do not deduplicate. Never replay an uncertain operation
      // until the backend advertises support for operation IDs.
      if (!supportsOperationIds && pending.some(request => request.sent)) discardPending('legacy_retry');
      if (pending.length === 0) {
        if (snapshot) setCartFromRemote(snapshot.cart);
        setIsSharedCartReady(true);
        return;
      }
      const request = pending[0];
      if (!request.epoch) request.epoch = snapshot.epoch;
      request.sent = true;
      persist();
      const flight = { request };
      inFlight = flight;
      cancelRetry();
      retryTimer = setTimeout(() => {
        if (!active || inFlight !== flight) return;
        inFlight = null;
        ready = false;
        setIsSharedCartReady(false);
        join();
      }, ACK_TIMEOUT_MS);
      socket.emit('cart:op', { op: request.op, epoch: request.epoch, opId: request.opId }, (response) => {
        if (!active || inFlight !== flight) return;
        cancelRetry();
        inFlight = null;
        if (!response || !response.ok) {
          if (response?.reason === 'stale_cart' || response?.reason === 'bad_op') {
            discardPending(response.reason === 'stale_cart' ? 'cart_changed' : 'invalid');
            acceptSnapshot(response);
            if (snapshot) setCartFromRemote(snapshot.cart);
            setIsSharedCartReady(Boolean(response.epoch && ready));
          } else {
            ready = false;
            setIsSharedCartReady(false);
            retryTimer = setTimeout(() => join(), 1500);
          }
          return;
        }
        pending.shift();
        // A local clear starts a new batch; operations selected after that
        // clear belong to its new epoch, unless another clear has intervened.
        if (request.op.type === 'clear' && response.appliedEpoch === response.epoch) {
          pending.forEach(next => {
            if (next.epoch === request.epoch) next.epoch = response.epoch;
          });
        }
        persist();
        acceptSnapshot(response);
        pump();
      });
    };

    join = () => {
      if (!active || !socket.connected) return;
      cancelRetry();
      ready = false;
      setIsSharedCartReady(false);
      const generation = ++connectionGeneration;
      retryTimer = setTimeout(() => join(), ACK_TIMEOUT_MS);
      socket.emit('join', { restaurantUrl, tableNo, clientId }, (response) => {
        if (!active || generation !== connectionGeneration) return;
        cancelRetry();
        if (!response?.ok || !response.epoch) {
          retryTimer = setTimeout(() => join(), 1500);
          return;
        }
        supportsOperationIds = response.supportsOperationIds === true;
        // A Redis expiry creates a new epoch with version zero. The join is
        // authoritative for that batch, while older same-epoch replies stay ignored.
        acceptSnapshot(response, true);
        ready = true;
        pump();
      });
    };
    socket.on('connect', join);

    socket.on('disconnect', () => {
      ready = false;
      setIsSharedCartReady(false);
      connectionGeneration++;
      cancelRetry();
      // Keep the original operation IDs and epoch, including the unacknowledged
      // head. Redis receipts make retrying it safe after reconnect/reload.
      inFlight = null;
      persist();
    });

    const receiveSnapshot = (payload) => {
      const previousEpoch = snapshot?.epoch;
      if (!acceptSnapshot(payload)) return;
      if (previousEpoch && previousEpoch !== snapshot.epoch) {
        discardPending('cart_changed');
      }
      if (!inFlight && pending.length === 0) {
        setCartFromRemote(snapshot.cart);
        setIsSharedCartReady(ready);
      }
    };
    socket.on('cart:updated', receiveSnapshot);
    socket.on('cart:cleared', receiveSnapshot);

    socket.on('partner:first-add', (payload) => {
      console.log('[sharedCart] received partner:first-add', payload, 'myClientId=', clientId);
      if (!payload || payload.fromClientId === clientId) {
        console.log('[sharedCart] skipped (self or empty)');
        return;
      }
      if (!payload.mainItem) {
        console.log('[sharedCart] skipped (no mainItem)');
        return;
      }
      console.log('[sharedCart] setting partnerFirstAdd');
      setPartnerFirstAdd({
        mainItem: payload.mainItem,
        ts: Date.now(),
      });
    });

    // 订阅本地 cart 变更 → 转成 op 发给服务器
    const unsubscribe = subscribeLocalChange(({ op }) => {
      if (!op) return;
      setIsSharedCartReady(false);
      pending.push({
        opId: `op_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`,
        epoch: snapshot?.epoch || null,
        op: JSON.parse(JSON.stringify(op)),
        createdAt: Date.now(),
        sent: false,
      });
      persist();
      pump();
    });

    return () => {
      active = false;
      cancelRetry();
      persist();
      if (unsubscribe) unsubscribe();
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantUrl, tableNo, enabled]);

  const clearPartnerFirstAdd = () => setPartnerFirstAdd(null);

  return { partnerFirstAdd, clearPartnerFirstAdd, isSharedCartReady, syncNotice };
}
